import gsap from 'gsap';

/**
 * Fond du hub : une grille de points qui s'écartent du curseur et s'illuminent
 * à son approche. Sans souris, un "faux curseur" se promène pour garder le fond vivant.
 * À placer sur .hub (le canvas est inséré en premier enfant).
 */
export default class HubBackground {
  constructor(element) {
    this.element = element;

    this.options = {
      spacing: 38, // distance entre les points (px)
      radius: 180, // rayon d'influence du curseur (px)
      push: 38, // déplacement maximal d'un point (px)
      dotSize: 1.5, // taille de base d'un point (px)
      color: '107, 223, 255', // cyan (r, g, b)
    };

    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'hubbg';
    this.canvas.setAttribute('aria-hidden', 'true');
    this.element.prepend(this.canvas);
    this.ctx = this.canvas.getContext('2d');

    this.pointer = { x: -9999, y: -9999, tx: -9999, ty: -9999 }; // position lissée / cible
    this.power = { value: 0.5 }; // 1 = vrai curseur, 0.5 = pilote automatique
    this.hasPointer = false;
    this.time = 0;

    this.tick = this.tick.bind(this);
    this.resize = this.resize.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onLeave = this.onLeave.bind(this);

    this.resize();
    this.ro = new ResizeObserver(this.resize);
    this.ro.observe(this.element);

    // Mouvement réduit : grille fixe, sans animation
    if (this.reduceMotion) {
      this.power.value = 0;
      this.draw(0, 0);
      return;
    }

    window.addEventListener('pointermove', this.onMove, { passive: true });
    document.documentElement.addEventListener('mouseleave', this.onLeave);
    gsap.ticker.add(this.tick);
  }

  /* ---------- Grille ---------- */

  resize() {
    const w = this.element.clientWidth;
    const h = this.element.clientHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    this.w = w;
    this.h = h;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const s = this.options.spacing;
    this.cols = Math.ceil(w / s) + 1;
    this.rows = Math.ceil(h / s) + 1;
    this.count = this.cols * this.rows;

    // Position d'origine de chaque point (grille centrée), décalage et vitesse
    this.ox = new Float32Array(this.count);
    this.oy = new Float32Array(this.count);
    this.dx = new Float32Array(this.count);
    this.dy = new Float32Array(this.count);
    this.vx = new Float32Array(this.count);
    this.vy = new Float32Array(this.count);

    const startX = (w - (this.cols - 1) * s) / 2;
    const startY = (h - (this.rows - 1) * s) / 2;
    for (let r = 0; r < this.rows; r++) {
      for (let c = 0; c < this.cols; c++) {
        const i = r * this.cols + c;
        this.ox[i] = startX + c * s;
        this.oy[i] = startY + r * s;
      }
    }

    if (this.reduceMotion) this.draw(0, 0);
  }

  /* ---------- Curseur ---------- */

  onMove(e) {
    const rect = this.element.getBoundingClientRect();
    this.pointer.tx = e.clientX - rect.left;
    this.pointer.ty = e.clientY - rect.top;

    if (!this.hasPointer) {
      this.hasPointer = true;
      this.pointer.x = this.pointer.tx;
      this.pointer.y = this.pointer.ty;
      gsap.to(this.power, { value: 1, duration: 0.8, overwrite: true });
    }
  }

  onLeave() {
    this.hasPointer = false;
    gsap.to(this.power, { value: 0.5, duration: 1.2, overwrite: true });
  }

  /* ---------- Boucle ---------- */

  tick(time, deltaTime) {
    const k = Math.min(deltaTime / 16.67, 2); // normalise selon le framerate
    this.time += deltaTime;

    // Sans souris : le faux curseur dessine une courbe lente dans la page
    if (!this.hasPointer) {
      this.pointer.tx = this.w * (0.5 + 0.38 * Math.sin(this.time * 0.00035));
      this.pointer.ty = this.h * (0.5 + 0.32 * Math.sin(this.time * 0.0005 + 1.3));
    }

    // Lissage du curseur
    const f = 1 - Math.pow(1 - 0.12, k);
    this.pointer.x += (this.pointer.tx - this.pointer.x) * f;
    this.pointer.y += (this.pointer.ty - this.pointer.y) * f;

    this.draw(k, this.time);
  }

  draw(k, t) {
    const { radius, push, dotSize, color } = this.options;
    const ctx = this.ctx;
    const px = this.pointer.x;
    const py = this.pointer.y;
    const power = this.power.value;
    const R2 = radius * radius;
    const damping = Math.pow(0.86, k);
    const TAU = Math.PI * 2;

    ctx.clearRect(0, 0, this.w, this.h);
    ctx.fillStyle = `rgb(${color})`;

    for (let i = 0; i < this.count; i++) {
      const ox = this.ox[i];
      const oy = this.oy[i];

      // Ressort de rappel vers la position d'origine
      let fx = -this.dx[i] * 0.07;
      let fy = -this.dy[i] * 0.07;

      // Répulsion + éclairage près du curseur (distance mesurée depuis l'origine : stable)
      const ddx = ox - px;
      const ddy = oy - py;
      const d2 = ddx * ddx + ddy * ddy;
      let near = 0;
      if (d2 < R2) {
        const d = Math.sqrt(d2) || 1;
        near = 1 - d / radius;
        const force = near * near * push * 0.07 * power;
        fx += (ddx / d) * force;
        fy += (ddy / d) * force;
      }

      this.vx[i] = (this.vx[i] + fx * k) * damping;
      this.vy[i] = (this.vy[i] + fy * k) * damping;
      this.dx[i] += this.vx[i] * k;
      this.dy[i] += this.vy[i] * k;

      // Petite vague ambiante + éclat près du curseur
      const alpha = Math.min(
        1,
        0.16 + 0.07 * Math.sin(t * 0.0016 + ox * 0.012 + oy * 0.016) + near * 0.75 * power
      );

      ctx.globalAlpha = alpha;
      ctx.beginPath();
      ctx.arc(ox + this.dx[i], oy + this.dy[i], dotSize + near * 1.8 * power, 0, TAU);
      ctx.fill();
    }

    // Halo doux sous le curseur
    if (power > 0) {
      const R = radius * 1.2;
      const g = ctx.createRadialGradient(px, py, 0, px, py, R);
      g.addColorStop(0, `rgba(${color}, ${0.1 * power})`);
      g.addColorStop(1, `rgba(${color}, 0)`);
      ctx.globalAlpha = 1;
      ctx.fillStyle = g;
      ctx.fillRect(px - R, py - R, R * 2, R * 2);
    }
  }

  destroy() {
    gsap.ticker.remove(this.tick);
    gsap.killTweensOf(this.power);
    window.removeEventListener('pointermove', this.onMove);
    document.documentElement.removeEventListener('mouseleave', this.onLeave);
    this.ro?.disconnect();
    this.canvas.remove();
  }
}
